/**
 * How long an aircraft may go without reporting before it drops off the live
 * map (#822).
 *
 * Aircraft used to be deleted from the database once they left contract, which
 * destroyed the record of their last known position and stranded their track
 * history. The live view is filtered on reporting recency instead, and this is
 * where that window is chosen.
 *
 * The choice is held per user per browser until the Preferences area (#823)
 * gives it a home on the user's account.
 */

import React, { useState } from 'react';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { dispatchAffLiveWindow } from '../../../components/explorers/affExplorer/affTrackEvent';

/** Days. Matches the server's own default and range (affRouter GET /ac). */
export const DEFAULT_LIVE_WINDOW_DAYS = 3;
export const MIN_LIVE_WINDOW_DAYS = 1;
export const MAX_LIVE_WINDOW_DAYS = 120;

export const liveWindowStorageKey = (userId: string | undefined): string =>
  `em3.aff.liveWindowDays.${userId ?? 'anonymous'}`;

/**
 * The same range the server enforces. A window outside it is refused here
 * rather than quietly rounded, so the control can never ask for something
 * GET /api/aff/ac would answer with a 400.
 */
const isUsableWindow = (days: number): boolean =>
  Number.isInteger(days) && days >= MIN_LIVE_WINDOW_DAYS && days <= MAX_LIVE_WINDOW_DAYS;

/**
 * Announce the window this user chose last time, once at startup.
 *
 * The control below is mounted only while its layer card is expanded, so on a
 * fresh page load nothing would speak for a remembered choice and the live
 * layer would open on the server's default. The drawer calls this from the
 * same effect that registers the layer providers — a child component could not,
 * because a child's mount effect runs before its parent's, and the provider
 * would not yet be listening.
 *
 * Nothing is announced unless there is a signed-in user with a usable stored
 * window: no user means no choice to honour, and an unusable one is left to the
 * server's own default rather than corrected here into something never chosen.
 */
export const announceStoredLiveWindow = (userId: string | undefined): void => {
  if (!userId) return;

  const stored = Number(localStorage.getItem(liveWindowStorageKey(userId)));
  if (!isUsableWindow(stored)) return;

  dispatchAffLiveWindow({ days: stored });
};

interface AffLiveWindowControlProps {
  layer: { id: string; name?: string };
}

export const AffLiveWindowControl: React.FC<AffLiveWindowControlProps> = () => {
  const { userInfo } = useFusionAuth() as { userInfo?: { sub?: string } };
  const storageKey = liveWindowStorageKey(userInfo?.sub);
  const [days, setDays] = useState<number>(() => {
    const parsed = Number(localStorage.getItem(storageKey));
    return isUsableWindow(parsed) ? parsed : DEFAULT_LIVE_WINDOW_DAYS;
  });

  const chooseWindow = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const chosen = Number(event.target.value);
    if (!isUsableWindow(chosen)) return;

    setDays(chosen);
    localStorage.setItem(storageKey, String(chosen));
    dispatchAffLiveWindow({ days: chosen });
  };

  return (
    <div className="aff-live-window-control">
      <label htmlFor="aff-live-window-days">Hide after (days)</label>
      <input
        id="aff-live-window-days"
        type="number"
        min={MIN_LIVE_WINDOW_DAYS}
        max={MAX_LIVE_WINDOW_DAYS}
        step={1}
        value={days}
        onChange={chooseWindow}
      />
    </div>
  );
};

export default AffLiveWindowControl;
